import React, { useState, useEffect } from "react";
import { Eye, EyeOff, ShieldCheck, User, Lock, Building2, HardHat, Loader2 } from "lucide-react";
import { clsx } from "clsx";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

// ✅ 기존 프로젝트의 AuthHook 임포트
import AuthHook from '@hooks/AuthHook'; 

export function Login() {
  const navigate = useNavigate();
  const { login } = AuthHook();

  // UI 탭 및 비밀번호 표시 상태 관리
  const [activeTab, setActiveTab] = useState("admin"); // "admin" or "user"
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");

  // ✅ react-hook-form 설정
  const { register, handleSubmit, setValue, reset, formState: { isSubmitting } } = useForm({
    defaultValues: { id: '', password: '', saveId: false },
  });

  // ✅ 아이디 저장 기능: 화면 렌더링 시 로컬스토리지에서 아이디 불러오기
  useEffect(() => {
    const isIdSaved = localStorage.getItem('isIdSaved') === 'true';
    if (isIdSaved) {
      setValue('id', localStorage.getItem('savedUserId') || '');
      setValue('saveId', true);
    }
  }, [setValue]);

  // ✅ 폼 제출(로그인 버튼 클릭 또는 엔터) 시 실행되는 함수
  const onSubmit = async ({ id, password, saveId }) => {
    setServerError('');
    try {
      // 1. API 통신 (백엔드 요구사항에 맞춰 { username, password } 형태로 전송)
      await login({ username: id, password, userType: activeTab });
      
      // 2. 아이디 저장 로직 처리
      if (saveId) {
        localStorage.setItem('savedUserId', id);
        localStorage.setItem('isIdSaved', 'true');
      } else {
        localStorage.removeItem('savedUserId');
        localStorage.setItem('isIdSaved', 'false');
      }

      // 3. 권한(Role) 저장
      localStorage.setItem('userRole', activeTab);

      // 4. 권한에 따른 페이지 이동
      if (activeTab === 'user') {
        navigate("/"); // 수행요원용 화면으로
      } else {
        navigate("/"); // 관리자용 화면으로
      }
      reset();

    } catch (err) {
      // 5. 에러 및 휴면 계정 처리
      const status = err?.response?.status;
      const msg = err?.response?.data?.errorMessage || err?.message || '로그인에 실패했습니다.';
      
      // 휴면 계정인 경우 활성화 페이지로 이동
      if (status === 428 || /휴면/.test(msg)) {
        navigate('/reactivate', { state: { memberId: id } });
        return;
      }
      setServerError(msg); // 화면에 에러 메시지 띄우기
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      {/* 🌟 마음에 드셨던 깔끔한 배경 블러 효과 */}
      <div className="absolute inset-0 z-0 bg-[url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-5 mix-blend-multiply" />
      
      <div className="relative z-10 w-full max-w-md animate-in fade-in zoom-in-95 duration-500">
        <div className="overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
          
          {/* 헤더 섹션 */}
          <div className="flex flex-col items-center bg-slate-900 px-8 py-10 text-center text-white">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-blue-600 shadow-lg shadow-blue-600/20">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">산업안전 관리시스템 포털</h1>
            <p className="mt-2 text-sm text-slate-400">
              역할을 선택하고 로그인해 주세요
            </p>
          </div>

          {/* 로그인 폼 섹션 */}
          <div className="p-8">
            {/* 역할 선택 탭 (UI) */}
            <div className="mb-8 flex rounded-lg bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => { setActiveTab("admin"); setServerError(""); }}
                className={clsx(
                  "flex flex-1 items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold transition-all",
                  activeTab === "admin"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                )}
              >
                <Building2 className="h-4 w-4" />
                관리자 로그인
              </button>
              <button
                type="button"
                onClick={() => { setActiveTab("user"); setServerError(""); }}
                className={clsx(
                  "flex flex-1 items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold transition-all",
                  activeTab === "user"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                )}
              >
                <HardHat className="h-4 w-4" />
                수행요원 로그인
              </button>
            </div>

            {/* ✅ 폼 제출 (react-hook-form의 handleSubmit 연결) */}
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              
              {/* 아이디 입력 */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">아이디 / 이메일</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="아이디를 입력하세요"
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 transition-colors"
                    disabled={isSubmitting}
                    {...register("id", { required: true })} // ✅ react-hook-form 연결
                  />
                </div>
              </div>

              {/* 비밀번호 입력 */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">비밀번호</label>
                  <a href="#" className="text-xs font-medium text-blue-600 hover:text-blue-700">비밀번호 찾기</a>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="비밀번호를 입력하세요"
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-12 text-sm text-slate-900 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 transition-colors"
                    disabled={isSubmitting}
                    {...register("password", { required: true })} // ✅ react-hook-form 연결
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 focus:outline-none"
                    disabled={isSubmitting}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* 에러 메시지 출력 */}
              {serverError && (
                <div className="text-sm font-medium text-red-500 text-center animate-in fade-in">
                  {serverError}
                </div>
              )}

              {/* 아이디 저장 체크박스 */}
              <div className="flex items-center pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600" 
                    disabled={isSubmitting} 
                    {...register("saveId")} // ✅ react-hook-form 연결
                  />
                  <span className="text-sm text-slate-600">아이디 저장</span>
                </label>
              </div>

              {/* 로그인 버튼 */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-70 transition-all"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    로그인 중...
                  </>
                ) : (
                  "로그인"
                )}
              </button>
            </form>

          </div>
        </div>

        {/* 푸터 */}
        <p className="mt-8 text-center text-xs font-medium text-slate-500">
          © 2026 Atos. All rights reserved.
        </p>
      </div>
    </div>
  );
}